import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileVideo, Plus, Trash2 } from 'lucide-react';
import { useDeleteSop, useSops } from '../hooks.ts';
import { SopStatusBadge } from '../components/SopStatusBadge.tsx';
import { formatWhen, STATUS_LABELS } from '../format.ts';
import { isSopEditable, LOCKED_SOP_MESSAGE, type SopSummary } from '../../../request/sop-synthesizer/sopSynthesizer.api.ts';
import { TENANT_PATHS } from '../../../routing/paths.ts';

const STATUS_FILTERS = ['generating', 'draft', 'in_review', 'changes_requested', 'approved', 'published'] as const;

export default function SopListPage() {
  const { data: sops = [], isLoading, error } = useSops();
  const remove = useDeleteSop();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sops.filter(
      (s: SopSummary) =>
        (!status || s.status === status) &&
        (!q || s.title.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q)),
    );
  }, [sops, search, status]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SOP Synthesizer</h1>
          <p className="text-sm text-gray-500">Standard operating procedures drafted from your walkthrough videos, with every statement cited.</p>
        </div>
        <Link
          to={TENANT_PATHS.SOP_SYNTHESIZER_NEW}
          className="inline-flex items-center gap-2 rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
        >
          <Plus className="h-4 w-4" /> New SOP
        </Link>
      </div>

      {sops.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title or description"
            aria-label="Search SOPs"
            className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">All statuses</option>
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-600">Could not load SOPs.</p>
      ) : sops.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
          <FileVideo className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Start your first SOP</h3>
          <p className="mt-1 text-sm text-gray-500">Pick a prepared walkthrough video and the SOP is drafted from what is said in it.</p>
          <Link
            to={TENANT_PATHS.SOP_SYNTHESIZER_NEW}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
          >
            <Plus className="h-4 w-4" /> New SOP
          </Link>
        </div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-gray-500">No SOPs match your filters.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Source video</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((s) => {
                const deletable = isSopEditable(s.status);
                return (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link className="font-medium text-gray-900 hover:underline" to={`/sop-synthesizer/${s.id}`}>{s.title}</Link>
                      <div className="max-w-md truncate text-xs text-gray-500">{s.description || 'No description'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{s.video_title ?? '—'}</td>
                    <td className="px-4 py-3"><SopStatusBadge status={s.status} run={s.latest_run} /></td>
                    <td className="px-4 py-3 text-gray-500">{formatWhen(s.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {confirmId === s.id ? (
                        <span className="inline-flex items-center gap-2 text-xs">
                          <span className="text-gray-600">Delete this SOP?</span>
                          <button
                            type="button"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate(s.id, { onSettled: () => setConfirmId(null) })}
                            className="rounded bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            Delete
                          </button>
                          <button type="button" onClick={() => setConfirmId(null)} className="text-gray-600 hover:underline">Cancel</button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => deletable && setConfirmId(s.id)}
                          aria-disabled={!deletable}
                          aria-label={deletable ? `Delete ${s.title}` : `Can't delete ${s.title}`}
                          title={deletable ? 'Delete' : LOCKED_SOP_MESSAGE}
                          className={`rounded p-1 ${deletable ? 'text-gray-500 hover:bg-red-50 hover:text-red-600' : 'cursor-not-allowed text-gray-300'}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
